/*
 * useRohdatenPanel.tsx - Rohdaten zu einem Entity anzeigen.
 *
 * Created by Dr. Maximillian Dornseif 2021-09
 * Copyright (c) 2021 HUDORA GmbH
 */

import { useQuery } from '@apollo/client'
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar'
import { Panel } from '@fluentui/react/lib/Panel'
import { HdErrorBoundary3 } from '@hudora/hd-error-boundary'
import { usePanel } from 'fluentui-hooks'
import gql from 'graphql-tag'
import React, { ReactElement } from 'react'
import ReactJson from 'react-json-view'

import { LadeChecker } from '../components/UiChecker'

/**
 * @deprecated
 */
export function useRohdatenPanel(
  daten: any,
  title?: string,
  schemaName?: string
): [ICommandBarItemProps, ReactElement] {
  const [openPanel, panelProps, isPanelOpen] = usePanel('Rohdaten')

  const commandBarItem: ICommandBarItemProps = {
    key: 'rohdaten',
    text: 'Datensatz-Rohdaten',
    ariaLabel: 'Rohdaten',
    iconOnly: true,
    iconProps: { iconName: 'PageData' },
    onClick: () => openPanel(),
  }

  const panel = (
    <Panel {...panelProps}>
      {isPanelOpen ? (
        <HdErrorBoundary3>
          <div data-testid="rohdatenpanel">
            <ReactJson
              src={daten}
              name={title || 'Rohdaten'}
              collapsed={1}
              collapseStringsAfterLength={30}
              displayDataTypes={false}
              iconStyle="square"
              theme="monokai"
            />
            <FieldScanner schemaName={schemaName} entity={daten?.entity || daten} />

            {process.env.NODE_ENV !== 'production' && daten?.entity?.id ? (
              <EntityDumpRaw id={daten?.entity?.id} />
            ) : null}
            {process.env.NODE_ENV !== 'production' && daten?.id ? <EntityDumpRaw id={daten?.id} /> : null}
          </div>
        </HdErrorBoundary3>
      ) : null}
    </Panel>
  )

  return [commandBarItem, panel]
}

export const INTROSPECTION_QUERY = gql`
  query IntrospectionQuery($name: String!) {
    __type(name: $name) {
      name
      fields {
        name
        type {
          name
          kind
          ofType {
            name
            kind
          }
        }
      }
    }
  }
`

export const FieldScanner = (props: { schemaName: string; entity: any }) => {
  const { loading, error, data } = useQuery(INTROSPECTION_QUERY, {
    variables: { name: props.schemaName },
    skip: !props.schemaName,
  })
  if (!data) {
    return <LadeChecker loading={loading} error={error} label="Introspection" />
  }
  const missing: string[] = []
  if (data?.__type?.fields) {
    for (const field of data?.__type?.fields || []) {
      if (props.entity?.[field.name] === undefined) {
        missing.push(field.name)
      }
    }
  }

  if (missing.length < 1) {
    return null
  }

  return (
    <>
      <LadeChecker loading={loading} error={error} label="Introspection" />
      <h2>Nicht abgefragte Felder</h2>
      <div
        style={{
          fontFamily: 'monospace',
          cursor: 'default',
          backgroundColor: 'rgb(39, 40, 34)',
        }}
      >
        <pre
          style={{
            color: 'rgb(249, 248, 245)',
            letterSpacing: '0.5px',
            verticalAlign: 'top',
            opacity: '0.85',
          }}
        >
          {missing.join('\n')}
        </pre>
      </div>
    </>
  )
}

export const RAW_QUERY = gql`
  query RawQuery($id: ID!) {
    rawDatastoreEntity(id: $id)
  }
`

export const EntityDumpRaw = (props: { id: string }) => {
  const { loading, error, data } = useQuery(RAW_QUERY, {
    variables: { id: props.id },
  })
  if (!data) {
    return <LadeChecker loading={loading} error={error} label="Lade Rohdaten" />
  }
  return (
    <>
      <LadeChecker loading={loading} error={error} label="Lade Rohdaten" />
      <h2>Datastore-Entity</h2>
      <ReactJson
        src={JSON.parse(data?.rawDatastoreEntity)}
        name="Datastore"
        collapsed={0}
        collapseStringsAfterLength={30}
        displayDataTypes={false}
        iconStyle="square"
        theme="monokai"
      />
    </>
  )
}
